import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const VehicleConfig = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  
  const [formData, setFormData] = useState({
    vehicle_number: '',
    vehicle_type: 'truck',
    capacity: 0,  // Changed from capacity_kg to capacity
    owner: '',
    insurance_expiry: '',
    registration_date: '',
    manufacturer: '',
    model: '',
    fuel_type: 'diesel',
    is_active: true
  });

  const VEHICLE_TYPES = [
    { value: 'truck', label: 'Truck' },
    { value: 'trailer', label: 'Trailer' },
    { value: 'container', label: 'Container' },
    { value: 'tanker', label: 'Tanker' },
    { value: 'pickup', label: 'Pickup' },
    { value: 'van', label: 'Van' },
    { value: 'other', label: 'Other' }
  ];

  const FUEL_TYPES = [
    { value: 'diesel', label: 'Diesel' },
    { value: 'petrol', label: 'Petrol' },
    { value: 'cng', label: 'CNG' },
    { value: 'electric', label: 'Electric' }
  ];

  const DOCUMENT_TYPES = [
    { key: 'rc_document', label: 'RC Document' },
    { key: 'insurance_document', label: 'Insurance Document' },
    { key: 'permit_document', label: 'Permit Document' },
    { key: 'fitness_document', label: 'Fitness Certificate' },
    { key: 'other_document', label: 'Other Document' }
  ];

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dispatch/vehicles/');
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.results) {
        data = response.data.results;
      }
      setVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehicle = null) => {
    if (vehicle) {
      setEditingVehicle(vehicle);
      setFormData({
        vehicle_number: vehicle.vehicle_number || '',
        vehicle_type: vehicle.vehicle_type || 'truck',
        capacity: parseFloat(vehicle.capacity) || 0,  // Changed from capacity_kg
        owner: vehicle.owner || '',
        insurance_expiry: vehicle.insurance_expiry || '',
        registration_date: vehicle.registration_date || '',
        manufacturer: vehicle.manufacturer || '',
        model: vehicle.model || '',
        fuel_type: vehicle.fuel_type || 'diesel',
        is_active: vehicle.is_active !== undefined ? vehicle.is_active : true
      });
      setSelectedFiles({});
    } else {
      setEditingVehicle(null);
      setFormData({
        vehicle_number: '',
        vehicle_type: 'truck',
        capacity: 0,  // Changed from capacity_kg
        owner: '',
        insurance_expiry: '',
        registration_date: '',
        manufacturer: '',
        model: '',
        fuel_type: 'diesel',
        is_active: true
      });
      setSelectedFiles({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVehicle(null);
    setSelectedFiles({});
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (docType) => (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [docType]: file
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.vehicle_number) {
        toast.error('Vehicle Number is required');
        return;
      }

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      Object.keys(selectedFiles).forEach(key => {
        if (selectedFiles[key]) {
          submitData.append(key, selectedFiles[key]);
        }
      });

      let response;
      if (editingVehicle) {
        response = await api.put(`/dispatch/vehicles/${editingVehicle.id}/`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Vehicle updated successfully');
      } else {
        response = await api.post('/dispatch/vehicles/', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Vehicle added successfully');
      }

      handleCloseDialog();
      fetchVehicles();
    } catch (error) {
      console.error('Error saving vehicle:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        toast.error('Failed to save vehicle');
      } else {
        toast.error('Failed to save vehicle');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        await api.delete(`/dispatch/vehicles/${id}/`);
        toast.success('Vehicle deleted successfully');
        fetchVehicles();
      } catch (error) {
        toast.error('Failed to delete vehicle');
      }
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Vehicle Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage company vehicles and their documents
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchVehicles} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Vehicle
            </Button>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Capacity (kg)</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Insurance</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No vehicles found. Click "Add Vehicle" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  vehicles.map((vehicle) => {
                    const hasDocuments = DOCUMENT_TYPES.some(doc => vehicle[doc.key]);
                    const insuranceValid = vehicle.insurance_expiry ? new Date(vehicle.insurance_expiry) >= new Date() : false;
                    
                    return (
                      <TableRow key={vehicle.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {vehicle.vehicle_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={vehicle.vehicle_type} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{vehicle.capacity || vehicle.capacity_kg || 0}</TableCell>
                        <TableCell>{vehicle.owner || 'N/A'}</TableCell>
                        <TableCell>
                          {vehicle.insurance_expiry ? (
                            <Box>
                              <Typography variant="caption" display="block">
                                {format(new Date(vehicle.insurance_expiry), 'dd/MM/yyyy')}
                              </Typography>
                              <Chip 
                                label={insuranceValid ? 'Valid' : 'Expired'} 
                                size="small" 
                                color={insuranceValid ? 'success' : 'error'}
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {hasDocuments ? (
                            <Tooltip title="Has documents">
                              <Chip 
                                label="Documents" 
                                size="small" 
                                color="primary"
                                icon={<DescriptionIcon />}
                              />
                            </Tooltip>
                          ) : 'No documents'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={vehicle.is_active ? 'Active' : 'Inactive'} 
                            size="small" 
                            color={vehicle.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenDialog(vehicle)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(vehicle.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Vehicle Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vehicle Number"
                value={formData.vehicle_number}
                onChange={handleChange('vehicle_number')}
                required
                helperText="Vehicle Registration Number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  value={formData.vehicle_type}
                  onChange={handleChange('vehicle_type')}
                  label="Vehicle Type"
                >
                  {VEHICLE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity (kg)"
                type="number"
                value={formData.capacity}
                onChange={handleChange('capacity')}
                InputProps={{ inputProps: { min: 0, step: 100 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Owner"
                value={formData.owner}
                onChange={handleChange('owner')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Insurance Expiry"
                type="date"
                value={formData.insurance_expiry}
                onChange={handleChange('insurance_expiry')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Date"
                type="date"
                value={formData.registration_date}
                onChange={handleChange('registration_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={handleChange('manufacturer')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                value={formData.model}
                onChange={handleChange('model')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Fuel Type</InputLabel>
                <Select
                  value={formData.fuel_type}
                  onChange={handleChange('fuel_type')}
                  label="Fuel Type"
                >
                  {FUEL_TYPES.map((fuel) => (
                    <MenuItem key={fuel.value} value={fuel.value}>
                      {fuel.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.is_active}
                  onChange={handleChange('is_active')}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Document Upload
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Upload vehicle documents (RC, Insurance, Permit, etc.)
              </Typography>
            </Grid>

            {DOCUMENT_TYPES.map((doc) => (
              <Grid item xs={12} sm={6} key={doc.key}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    sx={{ flex: 1 }}
                  >
                    {selectedFiles[doc.key] ? selectedFiles[doc.key].name : doc.label}
                    <input
                      type="file"
                      hidden
                      onChange={handleFileChange(doc.key)}
                    />
                  </Button>
                  {selectedFiles[doc.key] && (
                    <Chip 
                      label="Selected" 
                      size="small" 
                      color="success"
                    />
                  )}
                  {editingVehicle && editingVehicle[doc.key] && !selectedFiles[doc.key] && (
                    <Chip 
                      label="Uploaded" 
                      size="small" 
                      color="info"
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
          >
            {editingVehicle ? 'Update' : 'Add'} Vehicle
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VehicleConfig;